import React from "react";
import { Product, SelectedProduct } from "./types";
import { Search, Plus, Minus, ChevronLeft, ChevronRight } from "lucide-react";
import medicineIcon from "../../../../../assets/images/medicineIcon.png";
import { useAuth } from "../../../../../context/AuthContext";
import { Order } from "../../../Admin/StockOrder/OrderInfo/types";

interface ClinicProductsSectionProps {
  availableProducts: Product[];
  selectedProducts: SelectedProduct[];
  onAddProduct: (product: Product) => void;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveProduct: (productId: string) => void;
  onUpdateDiscount: (productId: string, discount: number) => void; // New prop for discount update
  loading?: boolean;
  error?: string | null;
  selectedOrder?: Order | null;
  onSearch: (searchTerm: string) => void;
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalItemsCount: number;
    totalPages: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

const ClinicProductsSection: React.FC<ClinicProductsSectionProps> = ({
  availableProducts,
  selectedProducts,
  onAddProduct,
  onUpdateQuantity,
  onRemoveProduct,
  onUpdateDiscount,
  selectedOrder,
  loading,
  error,
  onSearch,
  pagination,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchTimeout, setSearchTimeout] =
    React.useState<NodeJS.Timeout | null>(null);
  const { user } = useAuth();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(
      setTimeout(() => {
        onSearch(value);
      }, 500)
    );
  };

  React.useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const showOrderProductItems = selectedOrder?.productOrderItems?.length > 0;

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = parseInt(e.target.value);
    onItemsPerPageChange(value);
  };

  const handleDiscountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    compositeId: string
  ) => {
    const value = e.target.value;

    // Allow: empty string, numbers, and single decimal point
    if (value === "" || /^(\d+\.?\d*|\.\d+)$/.test(value)) {
      onUpdateDiscount(compositeId, parseFloat(e.target.value));
    }
  };

  // Function to calculate final price based on MRP and discount
  const calculateFinalPrice = (mrp: number, discount: number) => {
    return mrp * (1 - discount / 100);
  };

  // Helper function to calculate strip and tablet counts from selected quantity
  const calculateStripAndTabletCounts = (
    selectedQuantity: number,
    noOfItemsInPack: number
  ) => {
    const stripCount = Math.floor(selectedQuantity / noOfItemsInPack);
    const tabletCount = selectedQuantity % noOfItemsInPack;
    return { stripCount, tabletCount };
  };

  const getDaysRemaining = (expiryDate: string) => {
    if (!expiryDate) return null;

    const today = new Date();
    const expiry = new Date(expiryDate);

    // Reset time to avoid partial-day issues
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Handle strip count change
  const handleStripCountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    product: SelectedProduct
  ) => {
    const value = e.target.value;

    // Allow empty string or valid numbers
    if (value === "" || /^\d*$/.test(value)) {
      const stripCount = value === "" ? 0 : parseInt(value);

      const currentCounts = calculateStripAndTabletCounts(
        product.selectedQuantity,
        product.noOfItemsInPack
      );

      const newQuantity =
        stripCount * product.noOfItemsInPack + currentCounts.tabletCount;

      onUpdateQuantity(product.compositeId, newQuantity);
    }
  };

  // Handle tablet count change
  // Handle tablet count change
  const handleTabletCountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    product: SelectedProduct
  ) => {
    const tabletCount = parseInt(e.target.value) || 0;
    const currentCounts = calculateStripAndTabletCounts(
      product.selectedQuantity,
      product.noOfItemsInPack
    );
    const newQuantity =
      currentCounts.stripCount * product.noOfItemsInPack + tabletCount;

    onUpdateQuantity(product.compositeId, newQuantity);
  };

  return (
    <div>
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4 mt-4">
          {showOrderProductItems ? "Ordered Products" : ""}
        </h2>

        {showOrderProductItems ? (
          <>
            <div className="">
              {selectedOrder?.productOrderItems?.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-3 bg-white rounded-lg"
                >
                  {item.productImage && (
                    <img
                      src={item.productImage || medicineIcon}
                      alt={item.productName}
                      width={50}
                      height={50}
                      className="rounded border"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-gray-800">
                      {item.productName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.productDescription}
                    </p>
                    <p className="text-xs text-gray-500">
                      MRP: ₹{item.productPrice?.product_mrp} | Price: ₹
                      {item.productPrice?.final_selling_price} |
                      {item.productPrice?.selling_discount}% off
                    </p>
                  </div>
                  <div className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium">
                    {item.productsItemsCount || 1}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div>
              <div className="flex justify-between items-center mb-4 w-full">
                <h2 className="text-lg font-semibold text-gray-800">
                  Products
                </h2>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent bg-cyan-50"
                  />
                </div>
              </div>

              {loading && (
                <div className="text-center py-4">Loading products...</div>
              )}
              {error && (
                <div className="text-red-500 text-center py-4">{error}</div>
              )}

              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 mb-2 bg-cyan-50 p-2">
                <div className="col-span-3">Product Name</div>
                <div className="col-span-1">Batch</div>
                <div className="col-span-1">Expiry Date</div>
                {user?.role == "clinic" && (
                  <div className="col-span-1">Qty/Pack</div>
                )}
                {user?.role == "clinic" && (
                  <div className="col-span-1">MRP</div>
                )}
                {user?.role == "clinic" && (
                  <div className="col-span-1">MRP/Unit</div>
                )}
                <div
                  className={`${
                    user?.role == "clinic"
                      ? "col-span-1 flex justify-center"
                      : "col-span-1"
                  }`}
                >
                  Stips
                </div>
                <div
                  className={`${
                    user?.role == "clinic"
                      ? "col-span-1 flex justify-center"
                      : "col-span-2"
                  }`}
                >
                  Tablets
                </div>
                <div
                  className={`${
                    user?.role == "clinic" ? "col-span-2" : "col-span-2"
                  }`}
                >
                  Actions
                </div>
              </div>

              <div
                className="space-y-2 max-h-[200px] overflow-y-auto"
                style={{
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                }}
              >
                {availableProducts.map((product) => (
                  <div
                    key={product.compositeId}
                    className="grid grid-cols-12 gap-4 items-center p-2 rounded-lg"
                  >
                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-3" : "col-span-2"
                      } flex items-center gap-3`}
                    >
                      <img
                        src={product.product_image || medicineIcon}
                        alt={product.product_name}
                        className="w-10 h-10 object-cover rounded"
                      />
                      <div>
                        <div className="font-medium text-gray-800">
                          {product.product_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {product.manufacturer_name}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-1 flex items-center  ">
                      <div className="text-sm">{product.batch_number}</div>
                    </div>
                    <div className="col-span-1 flex items-center">
                      <div
                        className={`text-sm font-medium ${
                          getDaysRemaining(product?.expiry_date) !== null && (getDaysRemaining(product?.expiry_date) || 0 )< 15
                            ? "text-red-500"
                            : "text-gray-700"
                        }`}
                      >
                        {getDaysRemaining(product?.expiry_date) !== null
                          ? (getDaysRemaining(product?.expiry_date) || 0) > 0
                            ? `${getDaysRemaining(product?.expiry_date)} days left`
                            : "Expired"
                          : "N/A"}
                      </div>
                    </div>
                    {user?.role == "clinic" && (
                      <div className="col-span-1 flex items-center">
                        <div className="text-sm">{product.noOfItemsInPack}</div>
                      </div>
                    )}
                    {user?.role == "clinic" && (
                      <div className="col-span-1">
                        ₹ {product?.mrp?.toFixed(2)}
                      </div>
                    )}
                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-1" : "col-span-3"
                      }`}
                    >
                      <div className="font-medium items-center">
                        ₹ {product?.perPackPrice?.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expires:{" "}
                        {product.expiry_date
                          ? (() => {
                              const date = new Date(product.expiry_date);
                              const month = String(
                                date.getMonth() + 1
                              ).padStart(2, "0"); // Months are 0-indexed
                              const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year
                              return `${month}/${year}`;
                            })()
                          : "N/A"}
                      </div>
                    </div>
                    <div
                      className={`${
                        user?.role == "clinic"
                          ? "col-span-1 flex justify-center"
                          : "col-span-2"
                      } items-center`}
                    >
                      <div className="text-sm">
                        {Math.floor(
                          product.totalStock / product.noOfItemsInPack
                        )}
                      </div>
                    </div>
                    <div
                      className={`${
                        user?.role == "clinic"
                          ? "col-span-1 flex justify-center"
                          : "col-span-2"
                      }`}
                    >
                      <div className="text-sm">
                        {product.totalStock % product.noOfItemsInPack}
                      </div>
                    </div>
                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-2" : "col-span-2"
                      }`}
                    >
                      <button
                        onClick={() => onAddProduct(product)}
                        className="px-3 py-1 bg-white border border-gray-300 rounded-full text-sm hover:bg-gray-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Rows per page:</span>
                  <select
                    value={pagination?.itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="text-sm border rounded p-1"
                  >
                    <option value={2}>2</option>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Page {pagination?.currentPage} of {pagination?.totalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => onPageChange(pagination?.currentPage - 1)}
                      disabled={pagination?.currentPage === 1}
                      className={`p-1 rounded ${
                        pagination?.currentPage === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onPageChange(pagination?.currentPage + 1)}
                      disabled={
                        pagination?.currentPage === pagination?.totalPages
                      }
                      className={`p-1 rounded ${
                        pagination?.currentPage === pagination?.totalPages
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Products Section */}
            <h2 className="text-lg font-semibold text-gray-800 mb-4 mt-4">
              Selected Products
            </h2>

            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 mb-2 bg-cyan-50 p-2">
              <div
                className={`${
                  user?.role === "clinic" ? "col-span-2" : "col-span-2"
                }`}
              >
                Product Name
              </div>
              <div className="col-span-1">Batch</div>
              {user?.role === "clinic" && (
                <div className="col-span-1">Qty/Pack</div>
              )}
              <div className="col-span-1">MRP</div>
              <div className="col-span-1">Discount %</div>
              <div className="col-span-1">Selling Price</div>
              <div className="col-span-1">Strips</div>
              <div className="col-span-1">Tablets</div>
              <div className="col-span-1">Total</div>
              <div className="col-span-1">Actions</div>
              <div className="col-span-1">Tablets</div>
            </div>

            <div
              className="space-y-2 max-h-[300px] overflow-y-auto"
              style={{
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",
              }}
            >
              {selectedProducts.map((product) => {
                const finalPrice = calculateFinalPrice(
                  product.mrp,
                  product.sellingDiscount || 0
                );
                const perPackPrice = calculateFinalPrice(
                  product.perPackPrice,
                  product.sellingDiscount || 0
                );
                const totalPrice = perPackPrice * product.selectedQuantity;
                const { stripCount, tabletCount } =
                  calculateStripAndTabletCounts(
                    product.selectedQuantity,
                    product.noOfItemsInPack
                  );

                return (
                  <div
                    key={product.compositeId}
                    className="grid grid-cols-12 gap-4 items-center p-3 rounded-lg hover:bg-gray-50"
                  >
                    <div
                      className={`${
                        user?.role === "clinic" ? "col-span-2" : "col-span-2"
                      } flex items-center gap-3`}
                    >
                      <img
                        src={product.product_image || medicineIcon}
                        alt={product.product_name}
                        className="w-10 h-10 object-cover rounded"
                      />
                      <div>
                        <div className="font-medium text-gray-800">
                          {product.product_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {product.manufacturer_name}
                        </div>
                      </div>
                    </div>

                    <div className="col-span-1">
                      <div className="text-sm">{product.batch_number}</div>
                    </div>

                    {user?.role === "clinic" && (
                      <div className="col-span-1">
                        <div className="text-sm">{product.noOfItemsInPack}</div>
                      </div>
                    )}

                    <div className="col-span-1">
                      <div className="font-medium">
                        ₹ {product?.mrp?.toFixed(2)}
                      </div>
                    </div>

                    <div className="col-span-1">
                      <input
                        type="text"
                        min="0"
                        max="100"
                        inputMode="decimal"
                        value={product.sellingDiscount || 0}
                        onChange={(e) =>
                          handleDiscountChange(e, product.compositeId)
                        }
                        className="w-full p-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="col-span-1">
                      <div className="font-medium">
                        ₹ {finalPrice.toFixed(2)}
                      </div>
                    </div>

                    {/* Strip Count Input */}
                    <div className="col-span-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={stripCount}
                        onChange={(e) => handleStripCountChange(e, product)}
                        className="w-full p-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>

                    {/* Tablet Count Input */}
                    <div className="col-span-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        min="0"
                        max={product.noOfItemsInPack - 1}
                        value={tabletCount}
                        onChange={(e) => handleTabletCountChange(e, product)}
                        className="w-full p-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>

                    <div className="col-span-1">
                      <div className="font-medium text-blue-700">
                        ₹ {totalPrice.toFixed(2)}
                      </div>
                    </div>

                    <div className="col-span-1">
                      <button
                        onClick={() => onRemoveProduct(product.compositeId)}
                        className="px-2 py-1 bg-red-100 text-red-600 border border-red-200 rounded-full text-xs hover:bg-red-200 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="col-span-1">
                      <div className="font-medium text-sm text-blue-700">
                        {product.noOfItemsInPack > 1 ? (
                          <>
                            {stripCount * product.noOfItemsInPack + tabletCount}{" "}
                            tablets
                          </>
                        ) : (
                          <>{stripCount} Piece</>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClinicProductsSection;
